import { forwardRef, useContext, useEffect, useState } from "react";
import { PrivilageSettingProvider } from ".";
import { useSpring, animated } from "@react-spring/web";
import Collapse from "@mui/material/Collapse";
import { alpha, styled } from "@mui/material/styles";
import { TreeView } from "@mui/x-tree-view/TreeView";
import {
  TreeItem,
  treeItemClasses,
  TreeItemProps,
} from "@mui/x-tree-view/TreeItem";
import { IconButton } from "@mui/material";
// --------------- icons --------------
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
// import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { CheckedObject, PrivilageListType } from "./types";
import { useMutation } from "react-query";
import { savePrivilage } from "./services";
import usePopup from "../../../hooks/usePopup";
import useAuthentication from "../../../hooks/useAuthentication";
import { storageKey } from "../../../utilis/methods/storage/storage";
function TransitionComponent(props: any) {
  const style = useSpring({
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
}

const CustomTreeItem = forwardRef<HTMLLIElement, TreeItemProps>(
  (props, ref) => (
    <TreeItem {...props} TransitionComponent={TransitionComponent} ref={ref} />
  )
);

const StyledTreeItem = styled(CustomTreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.iconContainer}`]: {
    "& .close": {
      opacity: 0.3,
    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

function PrivilagesEdit() {
  const {
    privilages,
    selectedType,
    setTriggerSave,
    triggerSave,
    resetPrivilages,
    setSelectedType,
  } = useContext(PrivilageSettingProvider);
  const { setPermissions, setRefetchPermission, user } = useAuthentication();
  const [selected, setSelected] = useState<{ [key: string]: CheckedObject }>(
    {}
  );

  const { showSnackbar } = usePopup();

  const { mutate: save } = useMutation(savePrivilage, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        localStorage.setItem(storageKey.data, JSON.stringify(""));
        setPermissions([]);
        setSelected({});
        setSelectedType(null);
        resetPrivilages();
        setTriggerSave(false);
        setRefetchPermission(true);
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "success",
          duration: 3000,
        });
      } else {
        setTriggerSave(false);
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      setTriggerSave(false);
      showSnackbar({
        message: e?.response?.data?.responseMessage || "Something went wrong !",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });

  const formatObject = (
    params: PrivilageListType,
    checked: boolean
  ): { [key: string]: CheckedObject } => {
    const userId = selectedType?.userGroupId ? selectedType?.id || null : null;
    const userGroupId = !selectedType?.userGroupId
      ? selectedType?.id || null
      : null;
    if (Boolean(params.children)) {
      let obj: { [key: string]: CheckedObject } = {
        [`${params.id + params.parentId || 0}${params.name}`]: {
          id: params.id,
          checked,
          parentId: params.parentId,
          userId,
          userGroupId,
          permission:
            permissionTypes[params.name as keyof typeof permissionTypes] ||
            null,
          clinicId: params.clinicId,
        },
      };

      params.children.forEach((item) => {
        if (Boolean(item?.children?.length)) {
          obj = {
            ...obj,
            ...formatObject(item, checked),
          };
        } else {
          obj = {
            ...obj,
            [`${item.id + item.parentId || 0}${item.name}`]: {
              id: item.id,
              checked,
              parentId: item.parentId,
              userId,
              userGroupId,
              permission:
                permissionTypes[item.name as keyof typeof permissionTypes] ||
                null,
              clinicId: params.clinicId,
            },
          };
        }
      });

      return obj;
    } else {
      return {
        [`${params.id + params.parentId || 0}${params.name}`]: {
          id: params.id,
          checked,
          parentId: params.parentId,
          userId,
          userGroupId,
          permission:
            permissionTypes[params.name as keyof typeof permissionTypes] ||
            null,
          clinicId: params.clinicId,
        },
      };
    }
  };
  const handleClickChecked = (params: PrivilageListType) => {
    const update = formatObject(params, params.checked);
    setSelected((prev) => ({
      ...prev,
      ...update,
    }));
  };

  const handleSave = () => {
    const permissions = Object.entries(selected).map((i) => ({
      ...i[1],
      checked: i[1].checked ? 1 : 0,
      clinicId: user?.clinicId,
    }));
    if (Boolean(permissions.length)) {
      save(permissions as CheckedObject[]);
    } else {
      setTriggerSave(false);
      showSnackbar({
        message: "Please change some permissions",
        open: true,
        variant: "warning",
        duration: 3000,
      });
    }
  };

  const renderTree = (privilageList: PrivilageListType[]) => {
    if (Boolean(privilageList?.length)) {
      return privilageList.map((item, index) => (
        <StyledTreeItem
          disabled={
            user?.username.toLowerCase() == selectedType?.name && item.id == 49
          }
          key={index}
          nodeId={`${item.id + item.parentId || 0}${item.name}`}
          label={
            <span className="flex flex-row items-center">
              <IconButton
                disabled={
                  user?.username.toLowerCase() == selectedType?.name &&
                  item.id == 49
                }
                color="primary"
                onClick={() => {
                  handleClickChecked({
                    ...item,
                    checked: Boolean(
                      selected[`${item.id + item.parentId || 0}${item.name}`]
                    )
                      ? !Boolean(
                          selected[
                            `${item.id + item.parentId || 0}${item.name}`
                          ].checked
                        )
                      : !Boolean(item.checked),
                  });
                }}
              >
                {(
                  Boolean(
                    selected[`${item.id + item.parentId || 0}${item.name}`]
                  )
                    ? Boolean(
                        selected[`${item.id + item.parentId || 0}${item.name}`]
                          .checked
                      )
                    : Boolean(item.checked)
                ) ? (
                  <CheckBoxIcon />
                ) : (
                  <CheckBoxOutlineBlankIcon />
                )}
              </IconButton>
              <span>{item.name}</span>
            </span>
          }
        >
          {Boolean(item?.children?.length) ? renderTree(item.children) : null}
        </StyledTreeItem>
      ));
    } else {
      return <></>;
    }
  };

  useEffect(() => {
    if (triggerSave) {
      handleSave();
    }
  }, [triggerSave]);

  useEffect(() => {
    setSelected({});
  }, [selectedType]);

  return (
    <div>
      {privilages.permissions.length > 0 && (
        <TreeView
          aria-label="customized"
          defaultExpanded={[""]}
          defaultCollapseIcon={<KeyboardArrowDownIcon />}
          defaultExpandIcon={<KeyboardArrowRightIcon />}
          // defaultEndIcon={<IndeterminateCheckBoxOutlinedIcon />}
          sx={{ overflowX: "hidden" }}
        >
          {renderTree(privilages.permissions)}
        </TreeView>
      )}
    </div>
  );
}

export default PrivilagesEdit;

const permissionTypes = {
  Read: "Read",
  Write: "Write",
  Update: "Update",
  Delete: "Delete",
  Approval: "Approval",
};
